import { TypeChecker } from '@attain-apps/type-guards'

import { Raw } from './raw'
import * as types from './types'

/**
 * Additional information about a {@link WithInfo}, such as its {@link Type}
 */
export interface Info<out TType extends types.Type = types.Type> {
  /** @see {@link Type} */
  type?: TType
  /**
   * Number of decimals to show. Can only be passed when using a numerical type
   *
   * @example
   * 12345.6789 // precision = undefined
   * 12345.68 // precision = 2
   * 12345.7 // precision = 1
   * 12346 // precision = 0
   * 12350 // precision = -1
   * 12300 // precision = -2
   */
  precision?: TType extends types.Intrinsic.ForNumber ? number : undefined
}
export namespace Info {
  export const isInfo = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Info>({
      precision: TypeChecker.Factory.optional(TypeChecker.isNumber),
      type: types.Type.isType,
    }),
  )
  /**
   * Returns a {@link TypeChecker} that checks for a {@link Info} of the given
   * {@link Type}
   */

  export function getTypeCheckerFor<TType extends types.Type>(
    type: TType,
  ): TypeChecker<Info<TType>> {
    return TypeChecker.Factory.object<Info<TType>>({
      precision: TypeChecker.Factory.optional(
        TypeChecker.isNumber,
      ) satisfies TypeChecker<Info['precision']> as TypeChecker<any>,
      type: TypeChecker.Factory.exact(type),
    })
  }
}

/**
 * There are many cases where we need some more information about a particular
 * value. For example, is the given number a dollar value or a percentage?
 * Should we display a certain number of decimal places? Etc.
 *
 * This type provides the value ({@link Raw}) with the additional
 * information ({@link Info})
 */
export interface WithInfo<out TType extends types.Type = types.Type>
  extends Info<TType> {
  /**
   * The actual value represented by this {@link WithInfo} object
   *
   * @see {@link Raw}
   */
  raw: Raw<TType>
}
export namespace WithInfo {
  export const isWithInfo = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.intersection(
      Info.isInfo,
      TypeChecker.Factory.object<Difference<WithInfo, Info>>({
        raw: TypeChecker.Factory.union(
          TypeChecker.isString,
          TypeChecker.isNumber,
          TypeChecker.isBoolean,
        ),
      }),
    ),
  )
  /**
   * Returns a {@link TypeChecker} that checks for a {@link WithInfo} of the given
   * {@link Type}
   */
  export function getTypeCheckerFor<TType extends types.Type>(
    type: TType,
  ): TypeChecker<WithInfo<TType>> {
    return TypeChecker.Factory.intersection(
      Info.getTypeCheckerFor(type),
      TypeChecker.Factory.object<Pick<WithInfo<TType>, 'raw'>>({
        raw: Raw.getTypeCheckerFor(type),
      }),
    )
  }

  /**
   * {@link WithInfo} object with the given {@link Raw} value
   */
  export type Of<TRaw extends Raw> = Replace.Properties<
    WithInfo<types.Intrinsic.Of<TRaw>>,
    'raw',
    TRaw
  >

  /**
   * Creates {@link WithInfo} using the given {@link Raw} value with an inferred
   * {@link Type}
   *
   * @see {@link Type.inferFrom}
   */
  export function from<TRaw extends Raw>(raw: TRaw): Of<TRaw> {
    return { raw, type: types.Intrinsic.inferFrom(raw) }
  }

  /**
   * Discriminated union version of {@link WithInfo}
   */
  export type Strict<TType extends types.Type = types.Type> = TType extends any
    ? WithInfo<TType>
    : never
  export namespace Strict {
    export const isStrict: TypeChecker<Strict> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.union(
          ...types.Intrinsic.ALL.map(type => getTypeCheckerFor(type)),
        ) as TypeChecker<Strict>,
    ).usingAlias('WithInfo.Strict')
  }
}
